import React, { useState, useEffect } from "react";
import Cookies from "js-cookie"; // For managing cookies
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Link,
  FormGroup,
  FormControlLabel,
  Switch,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always required
    functional: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    // Check if the user has already set preferences
    const consentData = Cookies.get("cookie_preferences");
    if (consentData) {
      try {
        const parsedData = JSON.parse(consentData);
        setCookiePreferences(parsedData);
      } catch (e) {
        setOpen(true);
      }
    } else {
      setOpen(true);
    }
  }, []);

  const handleToggle = (category) => {
    if (category === "necessary") return; // Can't toggle necessary cookies
    setCookiePreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allAccepted);
  };

  const handleSavePreferences = () => {
    savePreferences(cookiePreferences);
  };

  const savePreferences = (preferences) => {
    // Save preferences as JSON string in a cookie
    Cookies.set("cookie_preferences", JSON.stringify(preferences), {
      expires: 365,
    });
    setOpen(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    savePreferences(onlyNecessary);
  };

  const cookieDescriptions = {
    necessary: "Essential for website functionality. Cannot be disabled.",
    functional: "Enable website functionality like saving preferences and personalization.",
    analytics: "Allow us to analyze usage to improve the website experience.",
    marketing: "Used for advertising and to display personalized content based on your browsing habits.",
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Cookie Preferences</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          We use cookies to enhance your experience, analyze site traffic, and personalize content.
          You can select which cookies you allow us to use. Note that necessary cookies are required for the website to function properly.
        </Typography>
        
        <Box sx={{ my: 2 }}>
          <Divider />
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={cookiePreferences.necessary}
                  disabled
                />
              }
              label={<Typography variant="subtitle1" fontWeight="bold">Necessary Cookies</Typography>}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
              {cookieDescriptions.necessary}
            </Typography>
            
            <Divider />
            
            <FormControlLabel
              control={
                <Switch
                  checked={cookiePreferences.functional}
                  onChange={() => handleToggle("functional")}
                />
              }
              label={<Typography variant="subtitle1" fontWeight="bold">Functional Cookies</Typography>}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
              {cookieDescriptions.functional}
            </Typography>
            
            <Divider />
            
            <FormControlLabel
              control={
                <Switch
                  checked={cookiePreferences.analytics}
                  onChange={() => handleToggle("analytics")}
                />
              }
              label={<Typography variant="subtitle1" fontWeight="bold">Analytics Cookies</Typography>}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
              {cookieDescriptions.analytics}
            </Typography>
            
            <Divider />
            
            <FormControlLabel
              control={
                <Switch
                  checked={cookiePreferences.marketing}
                  onChange={() => handleToggle("marketing")}
                />
              }
              label={<Typography variant="subtitle1" fontWeight="bold">Marketing Cookies</Typography>}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
              {cookieDescriptions.marketing}
            </Typography>
          </FormGroup>
        </Box>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Detailed Cookie Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              For more information about the cookies we use and your choices, please visit our{" "}
              <Link href="/privacy-policy" color="primary">
                Privacy Policy
              </Link>
              .
            </Typography>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between", px: 2, pb: 2 }}>
        <Button onClick={handleRejectAll} color="info">
          Reject All
        </Button>
        <Box>
          <Button 
            onClick={handleSavePreferences} 
            color="primary"
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Save Preferences
          </Button>
          <Button 
            onClick={handleAcceptAll} 
            color="primary" 
            variant="contained"
          >
            Accept All
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CookieConsent;